/**
 * The one way this repo talks to `dealbid-core`.
 *
 * Everything the public site shows is a read of the versioned `/v1` API, and everything a
 * visitor sends is one of the three anonymous public writes (canon
 * `docs/ARCHITECTURE.md` § Public web): nothing here caches, derives or invents state that
 * core owns. The base URL is build-time configuration validated in `src/config/env.ts`, so
 * a component can never assemble an origin of its own.
 *
 * Public reads and writes carry **no credentials**. `credentials: "omit"` is explicit
 * rather than left to the default, because core answers these endpoints without
 * `Access-Control-Allow-Credentials` (contracts/API.md § Public cross-origin policy) and a
 * request that sent a cookie would fail CORS for a reason that is hard to see from the
 * browser.
 */
import { apiUrl } from "../config/env";

/**
 * One field core refused, from the `fields` array on a `VALIDATION_FAILED` envelope
 * (contracts/API.md § Public submission shape).
 *
 * `field` is the dotted path of the offending input — `deal.dealUrl`, `seller.email` — and
 * the forms in this repo name their inputs with exactly those paths, so a server issue can
 * be put next to the input that produced it without a translation table that could drift.
 */
export interface ApiFieldIssue {
  field: string;
  code: string;
  message: string;
}

/**
 * A failed request, carrying core's machine-readable code so the UI keys off the code
 * rather than off a message string.
 *
 * `NETWORK_UNAVAILABLE`, `LIKELY_RATE_LIMITED` and `INVALID_RESPONSE` are this client's own
 * codes for "the request never reached core", "the request was turned away by something in
 * front of core" and "core answered something that is not the contract"; every other code
 * comes from core's error envelope. `RATE_LIMITED` is core's contract code but is emitted
 * by the Cloudflare edge rather than by a Worker, and a cross-origin browser cannot read
 * it — see {@link writeFailure}.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number | null;
  readonly requestId: string | null;
  /** Empty unless core answered `VALIDATION_FAILED`. */
  readonly fields: ApiFieldIssue[];

  constructor(
    code: string,
    message: string,
    options: {
      status?: number | null;
      requestId?: string | null;
      fields?: ApiFieldIssue[] | undefined;
    } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = options.status ?? null;
    this.requestId = options.requestId ?? null;
    this.fields = options.fields ?? [];
  }
}

/** Shape of core's error envelope (contracts/API.md § Error shape). */
interface ErrorEnvelope {
  error?: {
    code?: unknown;
    message?: unknown;
    requestId?: unknown;
    fields?: unknown;
  };
}

interface ReadEnvelope {
  code: string | null;
  requestId: string | null;
  fields: ApiFieldIssue[];
}

function readFieldIssues(value: unknown): ApiFieldIssue[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry): ApiFieldIssue[] => {
    if (typeof entry !== "object" || entry === null) return [];
    const { field, code, message } = entry as Record<string, unknown>;
    if (typeof field !== "string" || typeof code !== "string") return [];
    // A field message from core is written for a form ("Enter a valid URL, including
    // https://.") and is shown to the visitor. A missing one is not fatal: the form falls
    // back to its own copy rather than rendering an empty error.
    return [{ field, code, message: typeof message === "string" ? message : "" }];
  });
}

function readEnvelope(body: unknown): ReadEnvelope {
  const empty: ReadEnvelope = { code: null, requestId: null, fields: [] };
  if (typeof body !== "object" || body === null) return empty;
  const { error } = body as ErrorEnvelope;
  if (typeof error !== "object" || error === null) return empty;
  return {
    code: typeof error.code === "string" ? error.code : null,
    requestId: typeof error.requestId === "string" ? error.requestId : null,
    fields: readFieldIssues(error.fields),
  };
}

/**
 * Turns a non-2xx response into an `ApiError`.
 *
 * The top-level message is written for a visitor, not copied from core: core's messages are
 * contract text, and some of them describe conditions ("Origin is not allowed") a visitor
 * can do nothing about. Per-field messages *are* carried through — those are written for a
 * form and are the most precise thing anyone can say about the input.
 */
async function failure(response: Response, fallbackMessage: string): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const { code, requestId, fields } = readEnvelope(body);
  return new ApiError(code ?? "REQUEST_FAILED", fallbackMessage, {
    status: response.status,
    requestId,
    fields,
  });
}

function networkFailure(cause: unknown): never {
  // An aborted request is the caller unmounting or superseding it, not a failure the
  // visitor should be told about; it is rethrown so the caller can ignore it.
  if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
  throw new ApiError("NETWORK_UNAVAILABLE", "Dealbid couldn't reach the deals service.", {
    status: null,
  });
}

/** How long the reachability probe is given before it is treated as no answer. */
const PROBE_TIMEOUT_MS = 5_000;

/** Not every runtime the suite runs in implements `AbortSignal.timeout`. */
function probeTimeout(): AbortSignal | undefined {
  if (typeof AbortSignal === "undefined" || typeof AbortSignal.timeout !== "function") {
    return undefined;
  }
  return AbortSignal.timeout(PROBE_TIMEOUT_MS);
}

/**
 * Is `dealbid-core` answering this browser at all?
 *
 * Asked only after a public write has already failed at the transport layer, to tell the
 * two causes of that failure apart. See {@link writeFailure}.
 *
 * The probe is `GET /v1/board/today`, on an endpoint canon deliberately leaves unmetered
 * (`dealbid-core` `scripts/edge-rate-limit/policy.mjs` classifies it `anonymous-read`), so
 * asking cannot make a rate limit worse. It was the read the homepage already performed until
 * `WEB-039` moved that to build time; the endpoint stays the probe because what it needs to be
 * is public, unmetered and cheap, and it is still all three. This runs **only** after a public
 * write has already failed, so it is not a request a page load makes. Only a **readable success** counts as reachable — a 5xx, a
 * response the browser refuses to hand over, or no answer within
 * `PROBE_TIMEOUT_MS` all mean this browser cannot be shown to be talking to a healthy
 * core, and the visitor is told about an outage exactly as before.
 */
async function coreIsAnswering(): Promise<boolean> {
  const signal = probeTimeout();
  try {
    const response = await fetch(apiUrl("/v1/board/today"), {
      method: "GET",
      credentials: "omit",
      // A cached board would prove the browser reached core at some point in the past,
      // which is not the question being asked.
      cache: "no-store",
      headers: { accept: "application/json" },
      ...(signal ? { signal } : {}),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Turns a transport failure on a **public write** into the error the visitor should see.
 *
 * A `fetch` that rejects tells the caller nothing about why, and on these three endpoints
 * there are two causes that matter and one message that has been wrong for one of them:
 *
 * 1. Dealbid genuinely cannot be reached — offline, DNS, TLS, core down.
 * 2. The Cloudflare edge rate-limiting rule in front of core answered `429` (`OPS-004`).
 *    That response is generated before any Worker runs, so it carries no CORS headers, the
 *    browser refuses to hand it over, and the `fetch` rejects exactly as in (1). Canon
 *    `contracts/API.md` § Error shape records this.
 *
 * Telling a rate-limited visitor that Dealbid is unreachable is wrong twice over: the
 * service is fine, and the natural response to "couldn't reach" is to retry immediately,
 * which is the one behaviour the rule exists to stop.
 *
 * The two are separated by a question this client *can* answer: is core answering an
 * endpoint the rule does not cover? If it is, the site is up and it was this request that
 * was turned away. If it is not, nothing here can be shown to be reachable and the honest
 * answer is the outage message. The unknown case therefore keeps the pre-existing
 * behaviour, which is why a real outage never reports as a rate limit.
 *
 * What the inference does not settle is written into the message rather than out of it.
 * A readable board plus an unreadable write is *normally* the rate-limiting rule — it is
 * also what a broken origin allow-list would look like, since core answers a disallowed
 * origin without CORS headers too — so `LIKELY_RATE_LIMITED` says "normally means" and the
 * copy in `src/lib/api-messages.ts` claims no more than that. It also states no wait: the
 * mitigation window belongs to whichever rule fired, and this browser was never told which.
 */
async function writeFailure(cause: unknown): Promise<ApiError> {
  if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
  if (!(await coreIsAnswering())) {
    return new ApiError("NETWORK_UNAVAILABLE", "Dealbid couldn't reach the deals service.", {
      status: null,
    });
  }
  return new ApiError(
    "LIKELY_RATE_LIMITED",
    "Dealbid is reachable but turned this request away.",
    { status: null },
  );
}

function asJsonObject(body: unknown, status: number): Record<string, unknown> {
  if (typeof body !== "object" || body === null) {
    throw new ApiError("INVALID_RESPONSE", "The deals service returned an unreadable answer.", {
      status,
    });
  }
  return body as Record<string, unknown>;
}

/**
 * Reads one JSON resource.
 *
 * Failure is always an `ApiError`; a caller never has to tell a `TypeError` from a 500.
 */
export async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: "GET",
      credentials: "omit",
      headers: { accept: "application/json" },
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    networkFailure(cause);
  }

  if (!response.ok) throw await failure(response, "The deals service returned an error.");

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  return asJsonObject(body, response.status) as T;
}

/**
 * Sends one of the anonymous public writes.
 *
 * The `content-type` is required by core's public-mutation guard, and a JSON content type
 * on a cross-origin POST is never a simple request — so the browser preflights every one of
 * these and core answers it from the same origin allow-list. That is deliberate: the
 * preflight is what stops an arbitrary page from posting submissions in a visitor's name.
 *
 * The body is passed through as given. Core refuses unknown fields rather than ignoring
 * them, so callers in `src/api/writes.ts` build exactly the documented shape and nothing
 * else — an ignored `"status": "approved"` would look accepted.
 */
export async function postJson<T>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: "POST",
      credentials: "omit",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    throw await writeFailure(cause);
  }

  if (!response.ok) throw await failure(response, "Dealbid couldn't accept this right now.");

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }
  return asJsonObject(parsed, response.status) as T;
}
